/**
 * @fileOverview Redux store configuration with persistence layer for authentication state.
 * Implements a type-safe store with Redux Toolkit, Redux Persist, and custom middleware
 * configuration for non-serializable actions handling.
 *
 * @module store/storeConfig
 * @see {@link https://redux-toolkit.js.org/} Redux Toolkit Documentation
 * @see {@link https://github.com/rt2zz/redux-persist} Redux Persist Documentation
 */
import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import {
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE
} from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { authSlice, contextSlice, formSlice, loadingSlice, productSlice } from '.';

const persistenceConfiguration = {
  key: 'root',
  version: 1,
  storage
};

const persistedReducer = persistReducer(persistenceConfiguration, authSlice.reducer);

const rootReducer = combineReducers({
  auth: persistedReducer,
  formData: formSlice.reducer,
  productData: productSlice.reducer,
  loadingData: loadingSlice.reducer,
  contextData: contextSlice.reducer
});

export const store = configureStore({
  reducer: rootReducer,
  devTools: process.env.NODE_ENV !== 'production',
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER]
      }
    })
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
